import * as React from 'react';
import { memo, useCallback, useMemo } from 'react';
import { CellRendererProps, FlatList } from 'react-native';
import { StyleSheet } from 'react-native';
import { SvgProps } from 'react-native-svg';

import { EightIcon } from '@/assets/icons/eight';
import { FiveIcon } from '@/assets/icons/five';
import { FourIcon } from '@/assets/icons/four';
import { NineIcon } from '@/assets/icons/nine';
import { OneIcon } from '@/assets/icons/one';
import { SevenIcon } from '@/assets/icons/seven';
import { SixIcon } from '@/assets/icons/six';
import { TenIcon } from '@/assets/icons/ten';
import { ThreeIcon } from '@/assets/icons/three';
import { TwoIcon } from '@/assets/icons/two';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import BetrAnalytics from '@/feature/analytics/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { common } from '@/styles/styles';
import { isWeb, showScrollIndicator } from '@/utils/constants-platform-specific';

import { usePopularPlayers } from '../hooks/use-fantasy-lobby-query-with-variables';
import { extractPlayersListKey } from '../utils/lobbySections';
import { PlayerSmallCard } from './PlayerSmallCard';
import { PlayerSmallCardSkeleton } from './Skeletons/PlayerSmallCardSkeleton';

const numberOfSkeletonCards = isWeb ? 10 : 5;

export const PopularPlayersList = memo(({ title }: { title: string }) => {
    const { popularPlayers, hasNoData, fetching } = usePopularPlayers({
        // we use `cache-only` since this call is already made in the parent component (Lobby)
        requestPolicy: 'cache-only',
    });

    const top10PopularPlayers = useMemo(() => {
        return popularPlayers.slice(0, 10);
    }, [popularPlayers]);

    const trackOpenCardEvent = useCallback(() => {
        BetrAnalytics.trackEvent(AnalyticsEvent.POPULAR_PLAYERS_OPEN_CARD);
    }, []);

    const renderItem = useCallback(
        ({ item, index }: { item: PlayerWithTeamAndEvent; index: number }) => {
            if (fetching) {
                const position = index + 1;
                const hasTwoDigits = position > 9;
                return (
                    <PlayerSmallCardSkeleton
                        isFirstItem={index === 0}
                        isLastItem={index === numberOfSkeletonCards - 1}
                        showNumber={true}
                        hasTwoDigits={hasTwoDigits}
                        position={position}
                    />
                );
            }

            return (
                <PlayerSmallCard
                    player={item}
                    listOfPlayers={top10PopularPlayers}
                    index={index}
                    showNumber={true}
                    trackEvent={trackOpenCardEvent}
                    testID={`popularPlayerCard-${item.id}`}
                />
            );
        },
        [fetching, top10PopularPlayers, trackOpenCardEvent]
    );

    const CellRendererComponent = useCallback(
        ({ index, children, style, ...props }: CellRendererProps<PlayerWithTeamAndEvent>) => (
            <Box {...props} style={[style, { zIndex: 1 - index }]}>
                {children}
            </Box>
        ),
        []
    );

    if (!fetching && hasNoData) {
        return null;
    }

    const data = fetching ? new Array(numberOfSkeletonCards) : top10PopularPlayers;

    return (
        <Box style={[styles.root, common.noGrow]} paddingTop={'s16'} paddingBottom={'s20'}>
            <Text variant="headlineMedium" lineHeight={28} style={styles.title}>
                {title}
            </Text>

            <FlatList
                horizontal
                data={data}
                showsHorizontalScrollIndicator={showScrollIndicator}
                renderItem={renderItem}
                keyExtractor={(it, index) => extractPlayersListKey(it, index, fetching)}
                testID="topPicksFlatList"
                CellRendererComponent={CellRendererComponent}
            />
        </Box>
    );
});

export const FIRST_TOP10_CARD_SIZE = 80;
export const TOP10_CARD_SIZE = 88;

const NUMBER_ICON_MAP: Record<number, React.FC<SvgProps>> = {
    1: OneIcon,
    2: TwoIcon,
    3: ThreeIcon,
    4: FourIcon,
    5: FiveIcon,
    6: SixIcon,
    7: SevenIcon,
    8: EightIcon,
    9: NineIcon,
    10: TenIcon,
};

type Props = {
    position: number;
    isFirstItem: boolean;
    hasTwoDigits: boolean;
};

export const PopularPlayerNumberIcon = ({ position, isFirstItem, hasTwoDigits }: Props) => {
    const Icon = NUMBER_ICON_MAP[position];

    if (!Icon) {
        return null;
    }

    return (
        <Box style={styles.svgNumber} top={15} left={getNumberSvgXAxisPosition(isFirstItem, hasTwoDigits)}>
            <Icon />
        </Box>
    );
};

const getNumberSvgXAxisPosition = (isFirst: boolean, hasTwoDigits: boolean) => {
    if (hasTwoDigits) {
        return -10;
    }

    if (isFirst) {
        return 0;
    }

    return -5;
};

const styles = StyleSheet.create({
    root: {
        gap: 16,
        borderTopWidth: 1,
        borderColor: ds.colorValue.border.strong,
    },
    position: {
        fontWeight: 'normal',
        flexShrink: 1,
    },
    title: {
        fontSize: 22,
        paddingHorizontal: 16,
    },
    svgNumber: { position: 'absolute' },
    cardContainer: { alignItems: 'flex-end' },
});
